<script>
import { mapGetters } from 'vuex';
import { useAlert, useTrack } from 'dashboard/composables';
import CsatMetrics from './components/CsatMetrics.vue';
import NpsMetrics from './components/NpsMetrics.vue';
import CesMetrics from './components/CesMetrics.vue';
import CsatTable from './components/CsatTable.vue';
import CsatFilters from './components/Csat/CsatFilters.vue';
import { generateFileName } from '../../../../helper/downloadHelper';
import { REPORTS_EVENTS } from '../../../../helper/AnalyticsHelper/events';
import { FEATURE_FLAGS } from '../../../../featureFlags';
import V4Button from 'dashboard/components-next/button/Button.vue';
import ReportHeader from './components/ReportHeader.vue';

// Defaults para survey_settings - alinhados com Account#survey_settings_with_defaults.
// CSAT: meta 80% (% de notas 4-5). NPS: 50. CES: 3.0 (escala 1-7, menor é melhor).
const DEFAULT_TARGETS = { csat: 80, nps: 50, ces: 3.0 };

export default {
  name: 'CsatResponses',
  components: {
    CsatMetrics,
    NpsMetrics,
    CesMetrics,
    CsatTable,
    CsatFilters,
    ReportHeader,
    V4Button,
  },
  data() {
    return {
      pageIndex: 0,
      from: 0,
      to: 0,
      userIds: [],
      inbox: null,
      team: null,
      rating: null,
      // Aba ativa: csat | nps | ces. Default é o primeiro indicador habilitado.
      activeSurveyType: null,
    };
  },
  computed: {
    ...mapGetters({
      accountId: 'getCurrentAccountId',
      currentAccount: 'getCurrentAccount',
      isFeatureEnabledOnAccount: 'accounts/isFeatureEnabledonAccount',
    }),
    surveyConfig() {
      const cfg = this.currentAccount?.survey_settings || {};
      return {
        csat: {
          enabled: cfg.csat?.enabled !== false,
          targetScore: Number(cfg.csat?.target_score) || DEFAULT_TARGETS.csat,
        },
        nps: {
          enabled: cfg.nps?.enabled === true,
          targetScore: Number(cfg.nps?.target_score ?? DEFAULT_TARGETS.nps),
        },
        ces: {
          enabled: cfg.ces?.enabled === true,
          targetScore: Number(cfg.ces?.target_score ?? DEFAULT_TARGETS.ces),
        },
      };
    },
    availableSurveyTypes() {
      const types = [];
      if (this.surveyConfig.csat.enabled)
        types.push({ key: 'csat', label: this.$t('CSAT_REPORTS.HEADER') });
      if (this.surveyConfig.nps.enabled)
        types.push({ key: 'nps', label: this.$t('NPS_REPORTS.HEADER') });
      if (this.surveyConfig.ces.enabled)
        types.push({ key: 'ces', label: this.$t('CES_REPORTS.HEADER') });
      return types;
    },
    activeType() {
      // Se a aba persistida ainda existe nas opções, mantém. Caso contrário,
      // cai para a primeira habilitada.
      const types = this.availableSurveyTypes;
      if (!types.length) return null;
      if (
        this.activeSurveyType &&
        types.some(t => t.key === this.activeSurveyType)
      ) {
        return this.activeSurveyType;
      }
      return types[0].key;
    },
    activeTargetScore() {
      return this.surveyConfig[this.activeType]?.targetScore || null;
    },
    requestPayload() {
      return {
        from: this.from,
        to: this.to,
        user_ids: this.userIds,
        inbox_id: this.inbox,
        team_id: this.team,
        rating: this.rating,
        // Envia o tipo escolhido para o backend filtrar respostas e ratings_count
        // - sem isso, ratings_count traria CSAT misturado com NPS.
        survey_type: this.activeType,
      };
    },
    isTeamsEnabled() {
      return this.isFeatureEnabledOnAccount(
        this.accountId,
        FEATURE_FLAGS.TEAM_MANAGEMENT
      );
    },
    hasAnySurveyEnabled() {
      return this.availableSurveyTypes.length > 0;
    },
  },
  mounted() {
    this.$store.dispatch('agents/get');
  },
  methods: {
    getAllData() {
      try {
        this.$store.dispatch('csat/getMetrics', this.requestPayload);
        this.getResponses();
      } catch {
        useAlert(this.$t('REPORT.DATA_FETCHING_FAILED'));
      }
    },
    getResponses() {
      this.$store.dispatch('csat/get', {
        page: this.pageIndex + 1,
        ...this.requestPayload,
      });
    },
    downloadReports() {
      try {
        this.$store.dispatch('csat/downloadCSATReports', {
          fileName: generateFileName({ type: this.activeType, to: this.to }),
          ...this.requestPayload,
        });
      } catch (error) {
        useAlert(this.$t('REPORT.CSAT_REPORTS.DOWNLOAD_FAILED'));
      }
    },
    onPageNumberChange(pageIndex) {
      this.pageIndex = pageIndex;
      this.getResponses();
    },
    onSurveyTypeChange(key) {
      this.activeSurveyType = key;
      this.pageIndex = 0;
      // Re-busca métricas e tabela para o novo tipo se os filtros já carregaram.
      if (this.from !== 0 && this.to !== 0) this.getAllData();
    },
    onFilterChange({
      from,
      to,
      selectedAgents,
      selectedInbox,
      selectedTeam,
      selectedRating,
    }) {
      if (this.from !== 0 && this.to !== 0) {
        useTrack(REPORTS_EVENTS.FILTER_REPORT, {
          filterType: 'date',
          reportType: this.activeType,
        });
      }

      this.from = from;
      this.to = to;
      this.userIds = selectedAgents.map(el => el.id);
      this.inbox = selectedInbox?.id;
      this.team = selectedTeam?.id;
      this.rating = selectedRating?.value;

      this.getAllData();
    },
  },
};
</script>

<template>
  <ReportHeader :header-title="$t('SATISFACTION_REPORTS.HEADER')">
    <V4Button
      v-if="hasAnySurveyEnabled"
      :label="$t('CSAT_REPORTS.DOWNLOAD')"
      icon="i-ph-download-simple"
      size="sm"
      @click="downloadReports"
    />
  </ReportHeader>

  <!-- Sem nenhum indicador habilitado: orienta o admin -->
  <div
    v-if="!hasAnySurveyEnabled"
    class="mx-6 my-4 rounded-xl border border-dashed border-n-weak bg-n-solid-1 p-8 flex flex-col items-center text-center gap-3"
  >
    <span
      class="size-12 rounded-full bg-n-alpha-2 flex items-center justify-center"
    >
      <i class="i-lucide-smile-off size-6 text-n-slate-11" />
    </span>
    <div>
      <p class="text-sm font-medium text-n-slate-12 mb-1">
        {{ $t('SATISFACTION_REPORTS.EMPTY.TITLE') }}
      </p>
      <p class="text-xs text-n-slate-11 mb-0 max-w-md">
        {{ $t('SATISFACTION_REPORTS.EMPTY.BODY') }}
      </p>
    </div>
    <router-link
      :to="`/app/accounts/${accountId}/settings/satisfaction-survey`"
      class="text-xs text-n-brand hover:underline"
    >
      {{ $t('SATISFACTION_REPORTS.EMPTY.GO_TO_SETTINGS') }}
    </router-link>
  </div>

  <div v-else class="flex flex-col gap-6">
    <!-- Toggle por indicador. Só aparece quando >1 está habilitado. -->
    <div
      v-if="availableSurveyTypes.length > 1"
      class="px-6 -mt-2 flex items-center gap-1.5 bg-n-alpha-1 rounded-md w-fit p-1 ml-6"
    >
      <button
        v-for="t in availableSurveyTypes"
        :key="t.key"
        type="button"
        class="px-3 py-1.5 text-xs font-medium rounded-md transition-colors"
        :class="
          activeType === t.key
            ? 'bg-n-solid-1 text-n-slate-12 shadow'
            : 'text-n-slate-11 hover:text-n-slate-12'
        "
        @click="onSurveyTypeChange(t.key)"
      >
        {{ t.label }}
      </button>
    </div>

    <CsatFilters
      :show-team-filter="isTeamsEnabled"
      @filter-change="onFilterChange"
    />

    <!-- Cada indicador tem seu próprio bloco de métricas. CSAT mantém a tabela. -->
    <CsatMetrics
      v-if="activeType === 'csat'"
      :filters="requestPayload"
      :target-score="activeTargetScore"
    />
    <NpsMetrics
      v-else-if="activeType === 'nps'"
      :target-score="activeTargetScore"
    />
    <CesMetrics
      v-else-if="activeType === 'ces'"
      :target-score="activeTargetScore"
    />

    <CsatTable
      v-if="activeType === 'csat'"
      :page-index="pageIndex"
      @page-change="onPageNumberChange"
    />
  </div>
</template>
